import React from 'react';

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import makeStyles from '@mui/styles/makeStyles';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import GroupIcon from '@mui/icons-material/Group';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import useInterval from '../hooks/useInterval';
import AirchainAPI from '../utils/airchain-api';
import CopyButton from '../misc/CopyButton';
import Paper from '../misc/Paper';
import PaperHeader from '../misc/PaperHeader';
import Select from '../misc/Select';

// Additive panel, not part of upstream restreamer-ui: who else can log into
// this account (MULTI_USER_PLAN.md). Rendered as a tab inside Settings, since
// membership is account-wide configuration rather than something attached to
// the live channel -- it used to sit in Main's right-hand column purely to be
// somewhere a customer would find it.
const useStyles = makeStyles((theme) => ({
	teamBar: {
		'& .team-name': {
			fontSize: '.9rem',
		},
		'& .team-detail': {
			fontSize: '.75rem',
			color: theme.palette.text.secondary,
		},
	},
	inviteArea: {
		marginTop: '.6em',
		paddingTop: '.8em',
		borderTop: `1px solid ${theme.palette.background.light1}`,
	},
}));

// Membership changes when a person is invited or removed -- minutes or months
// apart, not seconds -- so this polls far slower than Captions' 1s live
// status. It polls at all (rather than only refetching after our own actions)
// because a second owner working in their own browser is exactly the
// situation this panel exists for.
const REFRESH_INTERVAL_MS = 30000;

// The invited person's own email is the only label available until they
// actually log in and we learn their Auth0 sub; after that the sub is all
// core ever tells us about them for an account whose email claim we never
// stored. Show whichever we have, rather than a blank cell.
function memberLabel(member) {
	return member.email || member.auth0_sub;
}

function expiryLabel(expiresAt) {
	return new Date(expiresAt * 1000).toLocaleDateString();
}

export default function Team(props) {
	const classes = useStyles();
	const { i18n } = useLingui();
	const [$team, setTeam] = React.useState(null);
	const [$loadError, setLoadError] = React.useState(false);
	const [$email, setEmail] = React.useState('');
	const [$role, setRole] = React.useState('member');
	// Whichever mutation is in flight, or null. Same optimistic-lock idea as
	// Captions' $pendingAction: one at a time, and the poll pauses while it
	// runs so a tick landing mid-request can't reconcile the list back to its
	// pre-change state for a frame.
	const [$pending, setPending] = React.useState(null);
	const [$actionError, setActionError] = React.useState(null);

	const refresh = React.useCallback(async () => {
		try {
			const team = await AirchainAPI.GetMembers(props.restreamer.GetAccessToken());
			setTeam(team);
			setLoadError(false);
		} catch (e) {
			setLoadError(true);
		}
	}, [props.restreamer]);

	useInterval(refresh, $pending !== null ? null : REFRESH_INTERVAL_MS);

	React.useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const runAction = async (name, action) => {
		setPending(name);
		setActionError(null);
		try {
			await action();
			await refresh();
		} catch (e) {
			// e.detail is the server's own explanation (see airchain-api.js) --
			// "this is the last owner", "already a member" and friends are all
			// things the operator can act on, so they're shown as written
			// rather than flattened into a generic failure.
			setActionError(e.detail || i18n._(t`Something went wrong. Try again.`));
		} finally {
			setPending(null);
		}
	};

	const handleInvite = () =>
		runAction('invite', async () => {
			await AirchainAPI.CreateInvitation(props.restreamer.GetAccessToken(), { email: $email, role: $role });
			setEmail('');
			setRole('member');
		});

	const handleRevoke = (invitation) =>
		runAction(`revoke:${invitation.token}`, () =>
			AirchainAPI.RevokeInvitation(props.restreamer.GetAccessToken(), invitation.token),
		);

	const handleRemove = (member) =>
		runAction(`remove:${member.auth0_sub}`, () =>
			AirchainAPI.RemoveMember(props.restreamer.GetAccessToken(), member.auth0_sub),
		);

	if ($team === null) {
		return (
			<Paper marginBottom="0">
				<PaperHeader title={<Trans>Team</Trans>} />
				<Stack alignItems="center" sx={{ py: 2 }}>
					{$loadError ? (
						<Typography className="team-detail">
							<Trans>Couldn't load the team list.</Trans>
						</Typography>
					) : (
						<CircularProgress size={22} />
					)}
				</Stack>
			</Paper>
		);
	}

	const isOwner = $team.me.role === 'owner';
	// Removing the last owner leaves an account nobody can administer, which
	// only an operator could repair -- the backend refuses it outright (409),
	// so the button is disabled here rather than offered and then rejected.
	const ownerCount = $team.members.filter((member) => member.role === 'owner').length;
	const isLastOwner = (member) => member.role === 'owner' && ownerCount <= 1;
	const busy = $pending !== null;

	return (
		<Paper marginBottom="0">
			<PaperHeader title={<Trans>Team</Trans>} />
			<Grid container spacing={1} className={classes.teamBar}>
				{$team.members.map((member) => (
					<Grid item xs={12} key={member.auth0_sub}>
						<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
							<Stack direction="row" alignItems="center" spacing={1}>
								<GroupIcon fontSize="small" />
								<Stack direction="column" spacing={0}>
									<Typography className="team-name">
										{memberLabel(member)}
										{member.auth0_sub === $team.me.auth0_sub && (
											<React.Fragment>
												{' '}
												<Trans>(you)</Trans>
											</React.Fragment>
										)}
									</Typography>
									<Typography className="team-detail">
										{member.role === 'owner' ? <Trans>Owner</Trans> : <Trans>Member</Trans>}
									</Typography>
								</Stack>
							</Stack>
							{isOwner && (
								<Button
									size="small"
									variant="outlined"
									color="secondary"
									disabled={busy || isLastOwner(member)}
									onClick={() => handleRemove(member)}
									title={
										isLastOwner(member)
											? i18n._(t`An account needs at least one owner — make someone else an owner first`)
											: i18n._(t`Remove this person's access`)
									}
								>
									<Trans>Remove</Trans>
								</Button>
							)}
						</Stack>
					</Grid>
				))}

				{$team.invitations.map((invitation) => (
					<Grid item xs={12} key={invitation.token || invitation.email}>
						<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
							<Stack direction="column" spacing={0}>
								<Typography className="team-name">{invitation.email}</Typography>
								<Typography className="team-detail">
									{invitation.role === 'owner' ? (
										<Trans>Invited as owner — expires {expiryLabel(invitation.expires_at)}</Trans>
									) : (
										<Trans>Invited as member — expires {expiryLabel(invitation.expires_at)}</Trans>
									)}
								</Typography>
							</Stack>
							{isOwner && (
								<Stack direction="row" alignItems="center" spacing={1}>
									{/* There is no mail infrastructure on this deployment
									(see control_service.py's invitation routes), so the
									owner is the delivery mechanism: they copy this link and
									send it however they already talk to the person. */}
									<CopyButton size="small" variant="outlined" color="secondary" value={invitation.accept_url}>
										<Trans>Copy link</Trans>
									</CopyButton>
									<Button
										size="small"
										variant="outlined"
										color="secondary"
										disabled={busy}
										onClick={() => handleRevoke(invitation)}
									>
										<Trans>Revoke</Trans>
									</Button>
								</Stack>
							)}
						</Stack>
					</Grid>
				))}

				{isOwner && (
					<Grid item xs={12} className={classes.inviteArea}>
						<Stack direction="column" spacing={1}>
							<TextField
								variant="outlined"
								fullWidth
								type="email"
								label={<Trans>Invite by email</Trans>}
								value={$email}
								onChange={(event) => setEmail(event.target.value)}
								disabled={busy}
							/>
							<Select label={<Trans>Role</Trans>} value={$role} onChange={(event) => setRole(event.target.value)} disabled={busy}>
								<MenuItem value="member">{i18n._(t`Member`)}</MenuItem>
								<MenuItem value="owner">{i18n._(t`Owner`)}</MenuItem>
							</Select>
							<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
								<Typography className="team-detail">
									<Trans>They'll get a link to copy and send — nothing is emailed.</Trans>
								</Typography>
								<Button size="small" variant="outlined" disabled={busy || $email.length === 0} onClick={handleInvite}>
									{$pending === 'invite' ? <Trans>Inviting…</Trans> : <Trans>Invite</Trans>}
								</Button>
							</Stack>
						</Stack>
					</Grid>
				)}

				{$actionError !== null && (
					<Grid item xs={12}>
						<Typography className="team-detail" color="error">
							{$actionError}
						</Typography>
					</Grid>
				)}
			</Grid>
		</Paper>
	);
}

Team.defaultProps = {
	restreamer: null,
};
